import { FC, ReactNode, useEffect, useMemo } from "react";
import { downloadExcel } from "react-export-table-to-excel";
import { Button, DatePicker, Form, Input, Popover, Select, Space } from "antd";
import moment from "moment";

import { isDateInRange } from "../../../../helper/dateTimeHelper";
import { DateFormat } from "../../../../constants";
import { useLocation } from "react-router-dom";
import { compareRequestStatus } from "../../utils";

const { RangePicker } = DatePicker;

interface ExportRepairToolsForm {
  createdDateTime: [moment.Moment, moment.Moment] | null;
  eRequestStatus: string | null;
  ECodeTCSSId: string;
}

interface ExportRepairToolsProps {
  data: Array<any>;
  initialValues?: any;
  exportContent: {
    fileName: string;
    sheet?: string;
    content: Array<{ header: string; dataIndex: string }>;
  };
  requestStatusOptions: Array<{ label: string; value: string }>;
  onChange?: (changedValues: any, allValues: ExportRepairToolsForm) => void;
  children?: ReactNode;
}

export const ExportRepairTools: FC<ExportRepairToolsProps> = ({
  data,
  initialValues = {},
  exportContent,
  requestStatusOptions,
  onChange = () => {},
  children,
}) => {
  const [form] = Form.useForm<ExportRepairToolsForm>();

  const { search } = useLocation();

  const eRequestStatus = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    return searchParams.get("status") || null;
  }, [search]);

  useEffect(() => {
    onChange(
      {},
      { eRequestStatus: eRequestStatus, createdDateTime: null, ECodeTCSSId: "" }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eRequestStatus, form]);

  const onSubmit = (values: ExportRepairToolsForm) => {
    let result = data;

    if (values?.eRequestStatus) {
      result = result.filter((e) => {
        return compareRequestStatus(
          e.eRequestStatus,
          values?.eRequestStatus || ""
        );
      });
    }
    if (values?.createdDateTime) {
      const [startDate, endDate] = values.createdDateTime;
      result = result.filter((e) => {
        const createdDate = moment(e.createdDateTime, DateFormat.DD_MM_YYY);
        return isDateInRange(
          createdDate,
          startDate.startOf("day"),
          endDate.endOf("day")
        );
      });
    }
    if (values.ECodeTCSSId) {
      result = result.filter((e: any) => {
        if (e.ECodeTCSSId) {
          return e.ECodeTCSSId.includes(values.ECodeTCSSId);
        }
        return false;
      });
    }

    const body = result.map((e) => {
      let obj: any = {};
      exportContent.content
        .map((e) => e.dataIndex)
        .forEach((key, i) => {
          obj[i] = e[key];
        });
      return obj;
    });

    downloadExcel({
      fileName: exportContent.fileName,
      sheet: exportContent?.sheet || "Sheet 1",
      tablePayload: {
        header: exportContent.content.map((e) => e.header),
        body,
      },
    });
  };

  const content = (
    <Form
      form={form}
      onFinish={onSubmit}
      initialValues={{ ...initialValues, eRequestStatus }}
      onValuesChange={onChange}
    >
      <Space direction="vertical" className="flex">
        <Space direction="vertical" className="flex">
          <div>Ngày tạo:</div>
          <Form.Item name="createdDateTime" style={{ marginBottom: "5px" }}>
            <RangePicker
              format={DateFormat.DD_MM_YYY}
              placeholder={["Ngày bắt đầu", "Ngày kết thúc"]}
            />
          </Form.Item>
        </Space>
        <Space direction="vertical" className="flex">
          <div>Trạng thái:</div>
          <Form.Item name="eRequestStatus" style={{ marginBottom: "5px" }}>
            <Select
              allowClear
              className="w-full"
              placeholder="Chọn trạng thái"
              options={requestStatusOptions}
            />
          </Form.Item>
        </Space>
        <Space direction="vertical" className="flex">
          <div>Mã thiết bị:</div>
          <Form.Item name="ECodeTCSSId" style={{ marginBottom: "5px" }}>
            <Input />
          </Form.Item>
        </Space>
        <Form.Item noStyle>
          <Button block htmlType="submit" type="primary">
            Xuất excel
          </Button>
        </Form.Item>
      </Space>
    </Form>
  );

  return (
    <Popover trigger={["click"]} placement="left" content={content}>
      {children}
    </Popover>
  );
};
